import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const ProjectUnmask = () => {
  const [selectedImage, setSelectedImage] = useState(null);

  // Gallery images with IDs for navigation
  const galleryImages = [
    { id: 1, url: '/images/projects/fichua-pazia1.png', caption: '', description: '' },
    { id: 2, url: '/images/projects/fichua-pazia2.png', caption: '', description: '' },
    { id: 3, url: '/images/projects/fichua-pazia3.png', caption: '', description: '' },
    { id: 4, url: '/images/projects/fichua-pazia4.png', caption: '', description: '' },
    { id: 5, url: '/images/projects/fichua-pazia5.png', caption: '', description: '' },
    { id: 6, url: '/images/projects/fichua-pazia6.png', caption: '', description: '' },
    { id: 8, url: '/images/projects/fichua-pazia8.jpeg', caption: 'Fichua Pazia Training workshops.', description: '' },
    { id: 9, url: '/images/projects/fichua-pazia9.jpeg', caption: 'Fichua Pazia Training workshops.', description: '' },
    { id: 10, url: '/images/projects/fichua-pazia10.jpeg', caption: 'Fichua Pazia Training workshops.', description: '' }
  ];

  const galleryRef = useRef(null);

  const scrollToGallery = () => {
    if (galleryRef.current) {
      galleryRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedImage) return;

      if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      } else if (e.key === 'Escape') {
        closeLightbox();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedImage]);

  // Touch/swipe navigation
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      goToNext();
    } else if (isRightSwipe) {
      goToPrevious();
    }
  };

  const openLightbox = (image) => {
    setSelectedImage(image);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setSelectedImage(null);
    document.body.style.overflow = 'auto';
  };

  const goToPrevious = () => {
    const currentIndex = galleryImages.findIndex(img => img.id === selectedImage.id);
    const previousIndex = currentIndex > 0 ? currentIndex - 1 : galleryImages.length - 1;
    setSelectedImage(galleryImages[previousIndex]);
  };

  const goToNext = () => {
    const currentIndex = galleryImages.findIndex(img => img.id === selectedImage.id);
    const nextIndex = currentIndex < galleryImages.length - 1 ? currentIndex + 1 : 0;
    setSelectedImage(galleryImages[nextIndex]);
  };

  return (
    <>
      {/* Hero Section */}
      <section className="project-detail-hero">
        <div className="container">
          <h1>Fichua Pazia</h1>
          <p className="project-subtitle">Safeguarding Research & Practice</p>
          <p className="project-tagline">Unmasking · Understanding · Transforming</p>
          <p className="project-institution">A ZIRAYA Institute project addressing systemic harm within Kenya's performing arts ecosystem — building safer spaces for those who make culture possible.</p>
          <div className="project-location">A ZIRAYA INSTITUTE PROJECT · NAIROBI</div>
        </div>
        <div className="hero-bg unmask-bg"></div>
      </section>

      {/* About the Project */}
      <section className="project-section highlight-box">
        <div className="container">
          <h2>About the Project</h2>
          <p className="project-subtitle-sm">The masks we wear to survive the work we love</p>
          <p>In Swahili, <strong>fichua</strong> means to uncover or reveal. <strong>Pazia</strong> is the curtain. Together, they name what this project sets out to do: draw back the curtain on the conditions — invisible and normalised — that expose performing artists, practitioners, and learners to harm.</p>
          <p>Fichua Pazia is a safeguarding research and practice initiative by ZIRAYA Institute. It documents, analyses, and responds to systemic vulnerabilities in Kenya's performing arts workspaces, centering the voices of those most affected and translating evidence into actionable frameworks for change.</p>
        </div>
      </section>

      {/* Three Pathways */}
      <section className="pathways-section">
        <div className="container">
          <h2 className="section-title-centered">Three Pathways to Safer Spaces</h2>
          <p className="pathways-subtitle">A framework built for this ecosystem</p>

          <div className="pathways-grid">
            <div className="pathway-card">
              <div className="pathway-number">I</div>
              <h3>Fichua / Reveal</h3>
              <h4>Unmasking</h4>
              <p>Documenting the specific forms of harm — sexual harassment, financial exploitation, psychological coercion — that persist under the silence expected of those who work in arts spaces.</p>
            </div>

            <div className="pathway-card">
              <div className="pathway-number">II</div>
              <h3>Elewa / Know</h3>
              <h4>Understanding</h4>
              <p>Building shared vocabulary and analysis. Naming power dynamics, structural enablers, and the cultural logics that make harm feel inevitable — so practitioners can recognise what they are experiencing.</p>
            </div>

            <div className="pathway-card">
              <div className="pathway-number">III</div>
              <h3>Badilisha / Change</h3>
              <h4>Transforming</h4>
              <p>Co-creating safeguarding protocols, reporting pathways, and training resources that are grounded in Kenyan performing arts realities — not imported frameworks that don't fit.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Navigation */}
      <div className="gallery-navigation">
        <button onClick={scrollToGallery} className="gallery-nav-btn">
          <i className="fas fa-images"></i>
          <span>Explore the Gallery <i className="fas fa-arrow-down"></i></span>
        </button>
      </div>

      {/* Arts Sector Focus */}
      <section className="arts-focus-section bg-dark">
        <div className="container">
          <div className="arts-focus-grid">
            <div className="arts-focus-item">
              <span className="focus-label">Arts Sector Focus</span>
              <span className="focus-value">Performing Arts</span>
            </div>
            <div className="arts-focus-item">
              <span className="focus-label">Primary Geography</span>
              <span className="focus-value">Kenya / Nairobi</span>
            </div>
            <div className="arts-focus-item">
              <span className="focus-label">R+P Method</span>
              <span className="focus-value">Research & Practice</span>
            </div>
            <div className="arts-focus-item">
              <span className="focus-label">Institution</span>
              <span className="focus-value">ZIRAYA Institute</span>
            </div>
          </div>
        </div>
      </section>

      {/* The Gap */}
      <section className="project-section">
        <div className="container">
          <h2>The Gap</h2>
          <p className="project-subtitle-sm">Harm that is named is harm that can be addressed</p>
          <p>Kenya's performing arts ecosystem is rich, diverse, and chronically under-resourced. Practitioners navigate precarious contracts, power-laden mentorship, informal training chains, and spaces where reporting mechanisms simply do not exist.</p>
          <p>The result: harm is routinised. It is absorbed into the cost of a creative career, particularly for women, young practitioners, and those outside established institutions. Fichua Pazia exists to interrupt that normalisation — through evidence, conversation, and structural alternatives.</p>
        </div>
      </section>

      {/* Our Approach */}
      <section className="approach-section bg-dark">
        <div className="container">
          <h2 className="section-title-centered">Our Approach</h2>
          <p className="approach-subtitle">Research that stays in the room</p>
          <p className="approach-intro">Fichua Pazia is practitioner-led from the ground up. Methods are participatory, outputs are practical, and accountability runs toward the community being researched — not only toward funders.</p>

          <div className="approach-grid">
            <div className="approach-card">
              <h3>Community Testimony</h3>
              <p>Structured conversations and testimonies with practitioners across theatre, music, dance, and spoken word — creating a Kenyan-specific evidence base.</p>
            </div>

            <div className="approach-card">
              <h3>Co-designed Protocols</h3>
              <p>Safeguarding frameworks developed collaboratively with practitioners and organisations — not handed down, but built together for real-world use.</p>
            </div>

            <div className="approach-card">
              <h3>Training & Capacity</h3>
              <p>Practical safeguarding literacy resources for companies, schools, and individual practitioners — how to recognise harm, how to report it, how to support.</p>
            </div>

            <div className="approach-card">
              <h3>Sustained Documentation</h3>
              <p>Ongoing recording of findings, progress, and gaps — building institutional memory that outlasts any single project cycle.</p>
            </div>
          </div>
        </div>
      </section>

      {/* The Institution */}
      <section className="project-section highlight-box">
        <div className="container">
          <h2>The Institution</h2>
          <p className="project-subtitle-sm">ZIRAYA Institute: research in the service of practice</p>
          <p>ZIRAYA Institute is a Nairobi-based creative research organisation dedicated to building knowledge, tools, and conditions that sustain Kenya's performing arts ecosystem. Fichua Pazia sits at the heart of its safeguarding portfolio — connecting to broader questions of livelihood, dignity, and structural equity for arts workers.</p>
          <p>The Institute is practitioner-founded, approaching every research question as someone who has stood in the same rooms, under the same pressures, as the communities it works with.</p>
        </div>
      </section>

      {/* YouTube Video */}
      <section className="project-section video-section">
        <div className="container">
          <h2>Watch: Fichua Pazia in Conversation</h2>
          <div className="video-container">
            <iframe
              width="100%"
              height="415"
              src="https://www.youtube.com/embed/uqscm1PL1p0?si=BIQIkfwsBCbHdbrP"
              title="Fichua Pazia in Conversation"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            ></iframe>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      {galleryImages.length > 0 && (
        <div className="journal-gallery" ref={galleryRef}>
          <h2>Voices from the Fichua Pazia Podcast</h2>
          <p className="gallery-subtitle">Experiences, insights, and reflections from creatives navigating Kenya's creative industry</p>
          <div className="gallery-grid">
            {galleryImages.map((image) => (
              <div
                key={image.id}
                className="gallery-item"
                onClick={() => openLightbox(image)}
              >
                <div className="gallery-image-wrapper">
                  <img
                    src={image.url}
                    alt={image.caption}
                    className="gallery-image"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/images/placeholder.jpg';
                    }}
                  />
                </div>
                {image.caption && (
                  <div className="gallery-caption">
                    <h4>{image.caption}</h4>
                    {image.description && <p>{image.description}</p>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Get Involved */}
      <section className="get-involved-section">
        <div className="container">
          <h2 className="section-title-centered">Get Involved</h2>
          <p className="get-involved-subtitle">Join the work of unmasking</p>
          <p className="get-involved-text">Whether you are a practitioner, researcher, organisation, or funder — there is a place for you in Fichua Pazia. Connect with us to learn how this work can serve your community.</p>
          <div className="get-involved-cta">
            <a href="mailto:projects@zirayainstitute.org?subject=Fichua%20Pazia%20Inquiry" className="btn btn-large">
              Connect With Us
            </a>
            <Link to="/" className="btn btn-secondary">Visit ZIRAYA Institute</Link>
          </div>
        </div>
      </section>

      {/* Footer Credit */}
      <section className="project-copyright">
        <div className="container">
          <p>ZIRAYA Institute · Fichua Pazia · Safeguarding Research & Practice · Nairobi, Kenya</p>
          <p>© ZIRAYA Institute · All rights reserved</p>
        </div>
      </section>



      {/* Enhanced Lightbox Modal with Navigation */}
      {selectedImage && (
        <div
          className="lightbox-modal"
          onClick={closeLightbox}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox}>
              <i className="fas fa-times"></i>
            </button>

            {/* Previous Button */}
            <button className="lightbox-nav prev" onClick={goToPrevious}>
              <i className="fas fa-chevron-left"></i>
            </button>

            {/* Next Button */}
            <button className="lightbox-nav next" onClick={goToNext}>
              <i className="fas fa-chevron-right"></i>
            </button>

            <div className="lightbox-image-wrapper">
              <img
                src={selectedImage.url}
                alt={`Fichua Pazia session ${selectedImage.id}`}
                className="lightbox-image"
              />
            </div>

            {/* Image counter */}
            <div className="lightbox-counter">
              {galleryImages.findIndex(img => img.id === selectedImage.id) + 1} / {galleryImages.length}
            </div>

            {(selectedImage.caption || selectedImage.description) && (
              <div className="lightbox-caption">
                <h3>{selectedImage.caption}</h3>
                <p>{selectedImage.description}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ProjectUnmask;